/**
 * OrgSwitcher.tsx — Organization switcher dropdown for the header bar
 *
 * Displays the current org name with a dropdown to switch orgs or
 * access platform admin. Shown in the top-right of AdminLayout.
 *
 * @since 2026-06-19 (SaaS conversion — Phase 8)
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgContext } from '../contexts/OrgContext';

export default function OrgSwitcher(): React.JSX.Element | null {
  const { activeOrg, availableOrgs, selectOrg, clearOrg, isPlatformAdmin } = useOrgContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!activeOrg && availableOrgs.length <= 1) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        <span className="h-2 w-2 rounded-full bg-green-400" />
        <span className="max-w-[160px] truncate">{activeOrg?.orgName ?? 'Select Org'}</span>
        <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Switch Organization
            </p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {availableOrgs.map((org) => (
              <button
                key={org.orgId}
                onClick={() => {
                  selectOrg(org.orgId);
                  setOpen(false);
                  navigate(`/org/${org.orgId}/overview`);
                }}
                className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-sm hover:bg-slate-50 ${
                  activeOrg?.orgId === org.orgId ? 'bg-cyan-50 text-cyan-700' : 'text-slate-700'
                }`}
              >
                <span className="truncate">{org.orgName}</span>
                <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">
                  {org.role}
                </span>
              </button>
            ))}
          </div>
          {(availableOrgs.length > 1 || isPlatformAdmin) && (
            <div className="border-t border-slate-100 px-3 py-2">
              {availableOrgs.length > 1 && (
                <button
                  onClick={() => {
                    clearOrg();
                    setOpen(false);
                    navigate('/select-org');
                  }}
                  className="w-full rounded-md px-2 py-1.5 text-left text-xs text-slate-500 hover:bg-slate-50"
                >
                  All organizations
                </button>
              )}
              {isPlatformAdmin && (
                <button
                  onClick={() => {
                    setOpen(false);
                    navigate('/platform/dashboard');
                  }}
                  className="w-full rounded-md px-2 py-1.5 text-left text-xs font-medium text-cyan-600 hover:bg-cyan-50"
                >
                  Platform Admin
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
